import { format, formatInTimeZone } from 'date-fns-tz';

// Helper function to format API date strings (assumes API timestamps are in UTC)
export const formatDate = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return formatInTimeZone(date, 'America/Los_Angeles', 'yyyy-MM-dd h:mm:ss a');
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
};

// Helper function to format database timestamps (assumes stored in UTC)
export const formatDbDate = (dateString = '') => {
  if (!dateString) return '';

  // If the string already contains a zone (Z or ±HH:MM) treat it as UTC/offset.
  const hasZone = /([+-]\d{2}:?\d{2}|Z)$/i.test(dateString);

  try {
    let date;
    if (hasZone) {
      // Parse as-is – Date will assume the embedded zone (Z = UTC)
      date = new Date(dateString);
    } else {
      // Naive timestamp ("YYYY-MM-DD HH:MM:SS"): stored coming from MySQL DATETIME
      // which discards the zone. These values are in UTC because the backend
      // inserted ISO strings that contained "Z" / offset and MySQL kept the
      // wall-clock portion.  Treat them as UTC then convert to Pacific.
      // Convert "YYYY-MM-DD HH:MM:SS"  →  "YYYY-MM-DDTHH:MM:SSZ" so JS parses as UTC.
      const isoUtc = dateString.replace(' ', 'T') + 'Z';
      date = new Date(isoUtc);
    }

    return formatInTimeZone(date, 'America/Los_Angeles', 'yyyy-MM-dd h:mm:ss a');
  } catch (error) {
    console.error('Error formatting database date:', error);
    return dateString;
  }
};

// Helper function to format duration (seconds to hours)
export const formatDuration = (seconds) => {
  if (!seconds) return '';

  try {
    const hours = Math.round((parseFloat(seconds) / 3600) * 100) / 100;
    return `${hours} hours`;
  } catch (error) {
    console.error('Error formatting duration:', error);
    return seconds;
  }
};

// Helper function to convert ISO 8601 datetime to MySQL format
export const convertDateTime = (dateTimeString) => {
  if (!dateTimeString) {
    return null;
  }

  try {
    const date = new Date(dateTimeString);
    return format(date, 'yyyy-MM-dd HH:mm:ss');
  } catch (error) {
    console.error('Error converting datetime:', error);
    return dateTimeString;
  }
};

// Helper function to get current time in Pacific timezone
export const getCurrentPacificTime = () => {
  return formatInTimeZone(new Date(), 'America/Los_Angeles', 'h:mm a');
};

// Helper function to calculate time difference in seconds
export const getTimeDifferenceSeconds = (targetDate, currentDate = new Date()) => {
  try {
    const target = new Date(targetDate);
    const current = new Date(currentDate);
    return Math.floor((target - current) / 1000);
  } catch (error) {
    console.error('Error calculating time difference:', error);
    return 0;
  }
};

// Helper function to format time difference as HH:MM:SS
export const formatTimeDifference = (seconds) => {
  const absSeconds = Math.abs(seconds);
  const days = Math.floor(absSeconds / 86400);
  const hours = Math.floor((absSeconds % 86400) / 3600);
  const minutes = Math.floor((absSeconds % 3600) / 60);
  const secs = absSeconds % 60;

  const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  return days > 0 ? `${days}days, ${timeStr}` : timeStr;
};